import { create } from "zustand";

import { ROUTES } from "@client/constants/routes";
import type { DmMessagePayload } from "@server/websocket/domains/dm/protocol";
import type { OnlineStatusType } from "@server/websocket/domains/presence/Presence";

export type DmPeer = {
	id: number;
	name: string;
	avatar: string;
	status?: OnlineStatusType;
	canMessage?: boolean;
};

type DmStore = {
	activePeerId: number | null;
	activePeer: DmPeer | null;
	messagesByPeer: Record<number, DmMessagePayload[]>;

	setActivePeer: (peer: DmPeer | null) => void;
	addMessage: (myUserId: number, message: DmMessagePayload) => void;
	prependMessages: (peerId: number, messages: DmMessagePayload[]) => void;
	setMessages: (peerId: number, messages: DmMessagePayload[]) => void;
	markMessagesRead: (messageIds: number[]) => void;
	reset: () => void;
};

function peerIdFor(
	message: DmMessagePayload,
	myUserId: number,
	activePeerId: number | null,
) {
	if (myUserId > 0) {
		return message.senderId === myUserId
			? message.receiverId
			: message.senderId;
	}
	if (
		activePeerId != null &&
		(message.senderId === activePeerId ||
			message.receiverId === activePeerId)
	) {
		return activePeerId;
	}
	return message.senderId;
}

export const EMPTY_DM_MESSAGES: DmMessagePayload[] = [];

export function isViewingDmPeer(peerId: number): boolean {
	if (useDmStore.getState().activePeerId !== peerId) {
		return false;
	}
	if (document.visibilityState !== "visible") {
		return false;
	}
	const current = window.location.pathname.split("/").slice(2).join("/");
	return current === ROUTES.FRIENDS || current === ROUTES.USER_SEARCH;
}

function mergeById(
	existing: DmMessagePayload[],
	incoming: DmMessagePayload[],
	at: "start" | "end",
) {
	const seen = new Set(existing.map((message) => message.id));
	const fresh = incoming.filter((message) => !seen.has(message.id));
	if (fresh.length === 0) {
		return existing;
	}
	const merged =
		at === "start" ? [...fresh, ...existing] : [...existing, ...fresh];
	return merged.sort((a, b) => a.id - b.id);
}

const initialState = {
	activePeerId: null as number | null,
	activePeer: null as DmPeer | null,
	messagesByPeer: {} as Record<number, DmMessagePayload[]>,
};

export const useDmStore = create<DmStore>((set) => ({
	...initialState,

	setActivePeer: (peer) =>
		set({
			activePeerId: peer?.id ?? null,
			activePeer: peer,
		}),

	addMessage: (myUserId, message) =>
		set((state) => {
			const peerId = peerIdFor(message, myUserId, state.activePeerId);
			const existing = state.messagesByPeer[peerId] ?? EMPTY_DM_MESSAGES;
			const merged = mergeById(existing, [message], "end");
			if (merged === existing) {
				return state;
			}
			return {
				messagesByPeer: {
					...state.messagesByPeer,
					[peerId]: merged,
				},
			};
		}),

	prependMessages: (peerId, messages) =>
		set((state) => {
			const existing = state.messagesByPeer[peerId] ?? EMPTY_DM_MESSAGES;
			const merged = mergeById(existing, messages, "start");
			if (merged === existing) {
				return state;
			}
			return {
				messagesByPeer: {
					...state.messagesByPeer,
					[peerId]: merged,
				},
			};
		}),

	setMessages: (peerId, messages) =>
		set((state) => ({
			messagesByPeer: {
				...state.messagesByPeer,
				[peerId]: [...messages].sort((a, b) => a.id - b.id),
			},
		})),

	markMessagesRead: (messageIds) => {
		if (messageIds.length === 0) {
			return;
		}
		const idSet = new Set(messageIds);
		set((state) => ({
			messagesByPeer: Object.fromEntries(
				Object.entries(state.messagesByPeer).map(
					([peerId, messages]) => [
						peerId,
						messages.map((message) =>
							idSet.has(message.id)
								? { ...message, isRead: true }
								: message,
						),
					],
				),
			),
		}));
	},

	reset: () => set(initialState),
}));
